/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { useIntl } from 'react-intl'

import { EuiButtonIcon } from '@elastic/eui'

import type { DeploymentGetResponse, TrafficFilterRulesetInfo } from '@modules/cloud-api/v1/types'
import { useDeleteHostedTrafficFilterAssociationMutation } from '@modules/network-security-lib/hooks/hosted'
import type { AnyUserProject } from '@modules/project-user-api/types'
import {
  usePatchElasticsearchProjectMutation,
  usePatchObservabilityProjectMutation,
  usePatchSecurityProjectMutation,
} from '@modules/project-user-lib/hooks/update'
import { addToast } from '@modules/cui/Toasts'

type Props = {
  ruleset: TrafficFilterRulesetInfo
} & (
  | {
      deployment: DeploymentGetResponse
      project?: never
    }
  | {
      project: AnyUserProject
      deployment?: never
    }
)

const RemoveButton: React.FunctionComponent<Props> = ({ deployment, project, ruleset }) => {
  const { formatMessage } = useIntl()

  const deleteHostedTrafficFilterAssociationMutation =
    useDeleteHostedTrafficFilterAssociationMutation()
  const patchElasticsearchProjectMutation = usePatchElasticsearchProjectMutation()
  const patchObservabilityProjectMutation = usePatchObservabilityProjectMutation()
  const patchSecurityProjectMutation = usePatchSecurityProjectMutation()

  const patchProjectMutation = project
    ? {
        elasticsearch: patchElasticsearchProjectMutation,
        observability: patchObservabilityProjectMutation,
        security: patchSecurityProjectMutation,
      }[project.type]
    : undefined

  const onError = () => {
    addToast({
      title: formatMessage({
        id: 'trafficFilterRemoveButton.error.title',
        defaultMessage: 'Warning',
      }),
      color: 'warning',
      iconType: 'warning',
      text: formatMessage({
        id: 'trafficFilterRemoveButton.error.description',
        defaultMessage: 'Could not remove traffic filter.',
      }),
    })
  }

  const remove = () => {
    if (deployment) {
      deleteHostedTrafficFilterAssociationMutation.mutate(
        {
          rulesetId: ruleset.id,
          deploymentId: deployment.id,
        },
        { onError },
      )
    }

    if (project) {
      patchProjectMutation?.mutate(
        {
          id: project.id,
          body: {
            traffic_filters: (project.traffic_filters ?? []).filter(({ id }) => id !== ruleset.id),
          },
        },
        { onError },
      )
    }
  }

  return (
    <EuiButtonIcon
      color='danger'
      iconType='cross'
      aria-label={formatMessage(
        { id: 'deploymentNetworkSecurity.list.remove', defaultMessage: 'Remove {name}' },
        { name: ruleset.name },
      )}
      onClick={remove}
    />
  )
}

export default RemoveButton
